import Box from '@mui/material/Box';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';
import IconAmenitiesAccessible from '../Icons/Amenities/IconAmenitiesAccessible';
import IconAmenitiesBlackwater from '../Icons/Amenities/IconAmenitiesBlackwater';
import IconAmenitiesDog from '../Icons/Amenities/IconAmenitiesDog';
import IconAmenitiesElectricity from '../Icons/Amenities/IconAmenitiesElectricity';
import IconAmenitiesFireplace from '../Icons/Amenities/IconAmenitiesFireplace';
import IconAmenitiesGarbage from '../Icons/Amenities/IconAmenitiesGarbage';
import IconAmenitiesGreyWater from '../Icons/Amenities/IconAmenitiesGreyWater';
import IconAmenitiesPlayground from '../Icons/Amenities/IconAmenitiesPlayground';
import IconAmenitiesPool from '../Icons/Amenities/IconAmenitiesPool';
import IconAmenitiesSauna from '../Icons/Amenities/IconAmenitiesSauna';
import IconAmenitiesShower from '../Icons/Amenities/IconAmenitiesShower';
import IconAmenitiesSignal from '../Icons/Amenities/IconAmenitiesSignal';
import IconAmenitiesWascher from '../Icons/Amenities/IconAmenitiesWascher';
import IconAmenitiesWater from '../Icons/Amenities/IconAmenitiesWater';
import IconAmenitiesWC from '../Icons/Amenities/IconAmenitiesWC';
import IconAmenitiesWiFi from '../Icons/Amenities/IconAmenitiesWiFi';
import IconSurroundingsKidsFriendly from '../Icons/Amenities/IconSurroundingsKidsFriendly';
import ItemChip from './ItemChip';

const getIcon = (optionKey) => {
  switch (optionKey) {
    case 'blackwater':
      return <IconAmenitiesBlackwater />;
    case 'dog':
      return <IconAmenitiesDog />;
    case 'electricity':
      return <IconAmenitiesElectricity />;
    case 'fireplace':
      return <IconAmenitiesFireplace />;
    case 'garbage':
      return <IconAmenitiesGarbage />;
    case 'greywater':
      return <IconAmenitiesGreyWater />;
    case 'shower':
      return <IconAmenitiesShower />;
    case 'signal':
      return <IconAmenitiesSignal />;
    case 'wascher':
      return <IconAmenitiesWascher />;
    case 'water':
      return <IconAmenitiesWater />;
    case 'wc':
      return <IconAmenitiesWC />;
    case 'wifi':
      return <IconAmenitiesWiFi />;
    case 'playground':
      return <IconAmenitiesPlayground />;
    case 'pool':
      return <IconAmenitiesPool />;
    case 'sauna':
      return <IconAmenitiesSauna />;
    case 'accessible':
      return <IconAmenitiesAccessible />;
    case 'kidsFriendly':
      return <IconSurroundingsKidsFriendly />;
    default:
      return null;
  }
};

/**
 * AmenitiesPropertyGroup renders a list of options as chips, indicating selected and non-selected items.
 *
 * It's used for displaying a group of selectable properties, such as amenities or activities,
 * in a form where users can see their current selections at a glance.
 *
 * Props:
 *   - id (string): A unique identifier for the group of options.
 *   - options (array): An array of option objects, each containing a `key` and `label`.
 *   - selectedOptions (array): An array of selected option keys.
 *   - intl (object): The internationalization object provided by react-intl's `injectIntl`.
 *   - showNotSelectedItems (bool): Flag to show or hide non-selected items.
 *
 * Each option is rendered as an `ItemChip` component, which is responsible for the actual display
 * of the chip.
 *
 * @param {object} props
 * @param {Array<{ key: string, label: string }>} props.options
 * @param {Array<string>} props.selectedOptions
 * @param {boolean} props.showNotSelectedItems
 * @returns {React.JSX.Element}
 *
 */
const AmenitiesPropertyGroupComponent = ({
  options,
  selectedOptions = [],
  showNotSelectedItems = false,
}) => {
  const intl = useIntl();
  return (
    <Box>
      {options.map((option) => (
        <ItemChip
          icon={getIcon(option.key)}
          isSelected={selectedOptions.includes(option.key)}
          key={`${option.key}`}
          label={intl.formatMessage({ id: option.label })}
          showNotSelectedItems={showNotSelectedItems}
        />
      ))}
    </Box>
  );
};

AmenitiesPropertyGroupComponent.displayName = 'AmenitiesPropertyGroup';
const AmenitiesPropertyGroup = memo(AmenitiesPropertyGroupComponent);

export default AmenitiesPropertyGroup;
